import type { MarketCountryCode } from '@backmarket/http-api'
import {
  type BuyBackQuoteListing,
  BuyBackQuoteListingStates,
  type BuyBackQuotePricingEstimation,
  LISTING_PUBLICATION_STATE,
  type PostBuyBackQuoteListing,
} from '@backmarket/http-api/src/api-specs-buyback/seller/buyback-quotes.types'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { isEqual } from '@backmarket/utils/object/isEqual'
import { toBcp47Locale } from '@backmarket/utils/string/toBcp47Locale'

export function getCountryCodeFromLocale(locale: string): MarketCountryCode {
  const { allMarkets } = useMarketplace()

  return Object.values(allMarkets).find(
    (market) => toBcp47Locale(market.defaultLocale) === toBcp47Locale(locale),
  )?.countryCode as MarketCountryCode
}

export function removeNonDecimalCharacters(value: string) {
  return value.replace(/\D/g, '')
}

/**
 * Get the estimate price to win for a specific grade
 *
 * @param pricingEstimations
 * @param grade
 */
export function getPricingEstimationByGrade(
  pricingEstimations: BuyBackQuotePricingEstimation[],
  grade: string,
): BuyBackQuotePricingEstimation | undefined {
  return pricingEstimations.find((estimation) => estimation.grade === grade)
}

export const removeDecimalsFromString = (value: string) => {
  if (typeof value !== 'string') {
    return ''
  }

  const number = Number(value)

  if (Number.isNaN(number)) {
    return ''
  }

  return Math.ceil(number).toString()
}

function matchListingState(
  postListing: PostBuyBackQuoteListing,
  getListing: BuyBackQuoteListing,
) {
  if (
    postListing.state === BuyBackQuoteListingStates.ENABLED &&
    getListing.state === LISTING_PUBLICATION_STATE.ONLINE
  ) {
    return true
  }

  if (
    postListing.state === BuyBackQuoteListingStates.DISABLED &&
    getListing.state === LISTING_PUBLICATION_STATE.OFFLINE
  ) {
    return true
  }

  return false
}

export function matchPostAndGetListings(
  listingsPostPayload: PostBuyBackQuoteListing[],
  listingsGetPayload: BuyBackQuoteListing[],
): boolean {
  return listingsPostPayload.every((postListing) => {
    const getListing = listingsGetPayload.find(
      (l) => l.grade === postListing.grade,
    )

    if (!getListing) {
      return false
    }

    const keys = (
      Object.keys(postListing) as (keyof PostBuyBackQuoteListing)[]
    ).filter((key) => key !== 'grade')

    return keys.every(
      (key) =>
        isEqual(postListing[key], getListing[key]) ||
        matchListingState(postListing, getListing),
    )
  })
}
